import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, CreditCard, Repeat, CheckCircle2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ConsentBox = ({ data, checked, onChange, testId = "autopay-consent" }) => (
  <label className="mt-3 block rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-950 cursor-pointer" data-testid={`${testId}-box`}>
    <span className="flex items-start gap-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-indigo-700" data-testid={`${testId}-checkbox`} />
      <span className="font-semibold flex items-center gap-1"><Repeat className="w-4 h-4" /> Save this payment method and automatically pay future invoices in this series</span>
    </span>
    <p className="mt-2 text-xs text-indigo-900/80 leading-relaxed" data-testid={`${testId}-text`}>{data.consent_text}</p>
  </label>
);

export const AutopayStatusBanner = ({ mandate, status }) => {
  if (!mandate && !status) return null;
  const text = status === "requires_action" ? "Your bank asked for extra confirmation — please pay below to complete this invoice." : status === "failed" ? "Automatic payment failed and autopay is paused. Please pay below." : status === "retry_scheduled" ? "The automatic payment did not go through; we will retry shortly. You can also pay now." : mandate ? `Autopay is on: ${mandate.label}. Future invoices in this series are charged automatically.` : null;
  if (!text) return null;
  const warn = ["requires_action", "failed", "retry_scheduled"].includes(status);
  return <div className={`rounded-2xl border p-4 text-sm flex items-center gap-2 ${warn ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-indigo-50 border-indigo-200 text-indigo-900"}`} data-testid="autopay-status-banner">{warn ? <ShieldCheck className="w-5 h-5" /> : <Repeat className="w-5 h-5" />}{text}</div>;
};

export const StripeAutopayCard = ({ token, card, balance, data }) => {
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const run = async (path, body) => {
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/public/pay/${token}/${path}`, { origin_url: window.location.origin, consent_version: data.consent_version, ...body });
      window.location.href = r.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Card payment is temporarily unavailable. Please use another payment option.");
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border-2 border-slate-700 bg-white p-5" data-testid="pay-card-section">
      <p className="font-bold text-slate-800 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Pay by credit / debit card</p>
      <p className="text-xs text-slate-500 mt-1">Secure checkout powered by Stripe (Visa, Mastercard, Amex, Discover).</p>
      <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="pay-card-fee-notice">
        <p className="font-semibold">Card processing fee: {card.fee_label}</p>
        <div className="mt-1 space-y-0.5 text-xs">
          <div className="flex justify-between"><span>Invoice balance</span><span>{money(balance)}</span></div>
          <div className="flex justify-between"><span>Processing fee ({card.fee_label})</span><span data-testid="pay-card-fee-amount">+{money(card.fee_amount)}</span></div>
          <div className="flex justify-between font-bold text-sm border-t border-amber-300 pt-1 mt-1"><span>Total charged to your card</span><span data-testid="pay-card-total">{money(card.total_with_fee)}</span></div>
        </div>
      </div>
      {data.autopay_providers?.includes("stripe") && !data.mandate && <ConsentBox data={data} checked={consent} onChange={setConsent} testId="stripe-autopay-consent" />}
      <button onClick={() => run(consent ? "checkout-autopay" : "checkout", consent ? {} : undefined)} disabled={busy} className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white py-3.5 font-semibold" data-testid="pay-card-button">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />} {busy ? "Redirecting to secure checkout…" : `Pay ${money(card.total_with_fee)} by card${consent ? " & enable autopay" : ""}`}
      </button>
      {data.autopay_providers?.includes("stripe") && !data.mandate && (
        <button onClick={() => { if (!consent) return alert("Please tick the autopay consent box first."); run("setup-autopay", {}); }} disabled={busy} className="mt-2 w-full text-xs text-indigo-700 underline" data-testid="autopay-setup-only-button">Save a card for autopay without paying now</button>
      )}
    </div>
  );
};

const loadPayPalSdk = (clientId) => new Promise((resolve, reject) => {
  if (window.paypal) return resolve(window.paypal);
  const s = document.createElement("script");
  s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture&components=buttons`;
  s.onload = () => resolve(window.paypal);
  s.onerror = () => reject(new Error("PayPal SDK failed to load"));
  document.body.appendChild(s);
});

export const PayPalCard = ({ token, data, balance, onPaid }) => {
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState("idle");
  const ref = useRef(null);
  const consentRef = useRef(consent);
  consentRef.current = consent;
  const pp = data.paypal;
  useEffect(() => {
    let cancelled = false;
    loadPayPalSdk(pp.client_id).then((paypal) => {
      if (cancelled || !ref.current) return;
      ref.current.innerHTML = "";
      paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "pill", label: "pay" },
        createOrder: async () => {
          const { data: r } = await axios.post(`${API}/public/pay/${token}/paypal/order`, { origin_url: window.location.origin, save_for_autopay: consentRef.current, consent_version: data.consent_version });
          return r.order_id;
        },
        onApprove: async (d) => {
          setState("capturing");
          try {
            const { data: r } = await axios.post(`${API}/public/pay/${token}/paypal/capture/${d.orderID}`);
            setState(r.payment_status === "paid" ? "paid" : "pending");
            onPaid && onPaid(r);
          } catch (e) {
            setState("failed");
          }
        },
        onError: () => setState("failed"),
      }).render(ref.current);
    }).catch(() => setState("sdk_failed"));
    return () => { cancelled = true; };
  }, [token, pp.client_id, data.consent_version, onPaid]);
  return (
    <div className="rounded-2xl border-2 border-[#003087] bg-white p-5" data-testid="pay-paypal-section">
      <p className="font-bold text-[#003087] flex items-center gap-2">Pay with PayPal {pp.mode === "sandbox" && <span className="text-[10px] uppercase bg-amber-100 text-amber-800 rounded px-1.5 py-0.5" data-testid="paypal-sandbox-badge">Sandbox</span>}</p>
      <p className="text-xs text-slate-500 mt-1">{pp.fee_amount > 0 ? `Total ${money(pp.total_with_fee)} (includes ${money(pp.fee_amount)} processing fee)` : `No fee — you pay ${money(balance)}.`}</p>
      {data.autopay_providers?.includes("paypal") && !data.mandate && <ConsentBox data={data} checked={consent} onChange={setConsent} testId="paypal-autopay-consent" />}
      {state === "paid" && <p className="mt-3 text-sm font-medium text-green-700 flex items-center gap-2" data-testid="paypal-paid"><CheckCircle2 className="w-4 h-4" /> PayPal payment received — thank you!</p>}
      {state === "capturing" && <p className="mt-3 text-sm text-slate-600 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Confirming your PayPal payment…</p>}
      {state === "failed" && <p className="mt-3 text-sm text-red-700" data-testid="paypal-failed">The PayPal payment was not completed. Please try again or use another option.</p>}
      {state === "sdk_failed" && <p className="mt-3 text-sm text-red-700">PayPal could not be loaded. Please use another payment option.</p>}
      <div ref={ref} className={`mt-3 ${state === "paid" ? "hidden" : ""}`} data-testid="paypal-buttons" />
    </div>
  );
};

export const AutopayActivation = ({ token, sessionId }) => {
  const [state, setState] = useState({ status: "checking" });
  useEffect(() => {
    let cancelled = false;
    const poll = async (tries) => {
      try {
        const { data } = await axios.get(`${API}/public/pay/${token}/autopay-status/${sessionId}`);
        if (cancelled) return;
        if (data.status === "active") return setState({ status: "active", label: data.label });
        if (data.status === "failed") return setState({ status: "failed" });
      } catch (e) { /* keep polling */ }
      if (tries >= 8) return setState({ status: "pending" });
      setTimeout(() => poll(tries + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [token, sessionId]);
  const cls = { checking: "bg-blue-50 border-blue-200 text-blue-900", active: "bg-indigo-50 border-indigo-300 text-indigo-900", pending: "bg-amber-50 border-amber-300 text-amber-900", failed: "bg-red-50 border-red-300 text-red-900" }[state.status];
  const text = { checking: "Saving your payment method for autopay…", active: `Autopay enabled with ${state.label}. A confirmation with a manage link is on its way to your email.`, pending: "Your payment method is still being confirmed. It will show as enabled here shortly.", failed: "The payment method could not be saved for autopay. Your payment (if any) is unaffected." }[state.status];
  return <div className={`rounded-2xl border p-4 text-sm font-medium flex items-center gap-2 ${cls}`} data-testid={`autopay-activation-${state.status}`}>{state.status === "checking" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Repeat className="w-5 h-5" />}{text}</div>;
};
